import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';

interface ActiveProjectsConfigData {
  excludeCompleted: boolean;
  selectedStages: string[];
}

interface ActiveProjectsConfigProps {
  value: ActiveProjectsConfigData;
  availableStages: string[];
  onChange: (config: ActiveProjectsConfigData) => void;
  variant?: 'full' | 'compact';
}

export const ActiveProjectsConfig: React.FC<ActiveProjectsConfigProps> = ({
  value,
  availableStages = [],
  onChange,
  variant = 'full'
}) => {
  // Ensure we have safe defaults
  const safeValue = {
    excludeCompleted: value?.excludeCompleted ?? true,
    selectedStages: value?.selectedStages ?? []
  };

  const handleExcludeCompletedChange = (checked: boolean) => {
    onChange({ ...safeValue, excludeCompleted: checked });
  };

  const handleStageAdd = (stage: string) => {
    if (stage && !safeValue.selectedStages.includes(stage)) {
      onChange({ ...safeValue, selectedStages: [...safeValue.selectedStages, stage] });
    }
  };

  const handleStageRemove = (index: number) => {
    const newStages = safeValue.selectedStages.filter((_, i) => i !== index);
    onChange({ ...safeValue, selectedStages: newStages });
  };

  return (
    <Box>
      {variant === 'full' && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Active Projects Criteria
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            Configure what projects are considered "Active" for the Active Projects tile.
            Active projects are those where today's date falls within the project date range.
          </Typography>
        </>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={safeValue.excludeCompleted}
            onChange={(e) => handleExcludeCompletedChange(e.target.checked)}
            size={variant === 'compact' ? 'small' : 'medium'}
          />
        }
        label="Exclude completed projects"
        sx={{ mb: 2 }}
      />
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Selected Stages ({safeValue.selectedStages.length})
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {safeValue.selectedStages.map((stage, index) => (
          <Chip
            key={index}
            label={stage}
            onDelete={() => handleStageRemove(index)}
            deleteIcon={<Delete />}
            size={variant === 'compact' ? 'small' : 'medium'}
          />
        ))}
      </Stack>
      <FormControl fullWidth size={variant === 'compact' ? 'small' : 'medium'}>
        <InputLabel>Add Stage</InputLabel>
        <Select
          value=""
          label="Add Stage"
          onChange={(e) => {
            const stage = e.target.value as string;
            handleStageAdd(stage);
          }}
        >
          {availableStages
            .filter(stage => stage)
            .map((stage) => (
              <MenuItem key={stage} value={stage}>
                {stage} {safeValue.selectedStages.includes(stage) ? '(already added)' : ''}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ActiveProjectsConfig; 